import React from 'react'
import { Box, Card, CardMedia, Grid, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { ShopHomePageResponse } from '../../models'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useNavigate } from 'react-router-dom'
import { CategoryType, ShopImageThumnbailSizes, SystemConfigureType } from '../../types'
import { getImageUrlBySize } from '../../utils'
import { E3more } from '@e3dc-react/icons'

interface NavigationCardProps {
  card: ShopHomePageResponse
}

const useStyles = makeStyles()(theme => ({
  cardWrapper: {
    width: '100%',
    [theme.breakpoints.only('sm')]: {
      '&:nth-child(odd)': {
        paddingRight: theme.spacing(1),
      },
      '&:nth-child(even)': {
        paddingLeft: theme.spacing(1),
      },
    },
    '&:hover': {
      cursor: 'pointer',
    },
  },
  card: {
    position: 'relative',
    width: '100%',
    height: '15vw',

    padding: 0,
    display: 'block',
    [theme.breakpoints.down('md')]: {
      height: '30vw',
      width: '100%',
    },
    [theme.breakpoints.down('sm')]: {
      height: '40vw',
    },
  },
  cardBackground: {
    width: '100%',
    height: '100%',
    transition: 'all 0.5s ease-in-out',
    '&:hover': {
      transform: 'scale(1.03)',
    },
  },
  textContainer: {
    position: 'absolute',
    bottom: theme.spacing(4),
    marginLeft: theme.spacing(2),
  },
  cardTitle: {
    position: 'relative',
    fontSize: '24px',
    fontWeight: 400,
    lineHeight: '32px',
    color: theme.palette.common.white,
    zIndex: 1,
  },
  cardSubtitle: {
    fontSize: '14px',
    fontWeight: '400',
    color: theme.palette.common.white,
    paddingTop: theme.spacing(2),
    zIndex: 1,
    position: 'relative',
  },
  moreInfoIcon: {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '50%',
    width: '18px',
    height: '18px',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(1),
  },
}))

const NavigationCard: React.VFC<NavigationCardProps> = ({ card }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  /*TODO: Get Image form Thumbnail instead of direct url */
  const isXs = useMediaQuery(theme.breakpoints.only('xs'))
  const categoryImageUrl = getImageUrlBySize(card.media?.thumbnails, ShopImageThumnbailSizes.Category)
  const handleNavigate = (card: ShopHomePageResponse): void => {
    if (card.type === CategoryType.QntmSystemConfiguratioName) {
      return navigate(`/shop/system-configure/${SystemConfigureType.QntmSystem}`)
    }

    if (card.type === CategoryType.SystemConfigurationName) {
      return navigate(`/shop/system-configure/${SystemConfigureType.NormalSystem}`)
    }

    if (card.type === CategoryType.FavoriteSystemConfigurationName) {
      return navigate('/shop/favorite-system')
    }

    return navigate(`/shop/category/${card.id}/${card.translated.name}`)
  }

  return (
    <Grid className={classes.cardWrapper} item xs={12} sm={6}>
      <Card
        className={classes.card}
        onClick={() => {
          handleNavigate(card)
        }}
      >
        {card?.media?.url && (
          <CardMedia className={classes.cardBackground} component="img" image={categoryImageUrl} alt={t('shop.home-page-card.system-configure')} />
        )}
        <Box className={classes.textContainer}>
          <Typography className={classes.cardTitle}>
            {card.translated.name}
            {card.translated?.description && isXs && (
              <Tooltip enterTouchDelay={0} placement="top" title={<div dangerouslySetInnerHTML={{ __html: card.translated.description }} />}>
                <IconButton className={classes.moreInfoIcon}>
                  <E3more color="inherit" sx={{ transform: 'rotate(-90deg )', width: '16px', height: '16px' }} />
                </IconButton>
              </Tooltip>
            )}
          </Typography>
          {card.translated?.description && !isXs && (
            <Typography className={classes.cardSubtitle}>
              <div dangerouslySetInnerHTML={{ __html: card.translated.description }} />
            </Typography>
          )}
        </Box>
      </Card>
    </Grid>
  )
}

export default NavigationCard
